/**
 * OrdersApiService (Angular 18) — HTTP wrappers for the /api/orders,
 * /api/rebook, /api/willCall, /api/orderrequest, /api/orderforms and
 * /api/checkout domains.
 *
 * Replaces the AngularJS legacy service at:
 *   apps/b2b-ui/common/services/api/ordersApi/ordersApi.service.js
 *
 * Exposed as `apiService.orders.*` through the ApiService facade (downgraded
 * under the legacy name for the 6 b2b-ui consumers).
 *
 * Sub-namespace strategy: **Option A** — every sub-namespace is a `readonly`
 * object literal on the class, grouped with section headers. The criterion
 * for promoting a literal to a separate `@Injectable` sub-service is
 * documented in MIGRATION_GUIDE.md → "Sub-namespace pattern".
 *
 * Notes
 *  - Legacy mixes `/v1/` and `/v2/` URLs; each one is hardcoded so a search
 *    from the Network tab lands you here.
 *  - `csv({ useForm: true })` issues a full-page browser form POST (download
 *    flow) instead of a JSON XHR. Preserved with `document.createElement` —
 *    no jQuery dependency.
 *  - `update()` and `partners.create()` propagate `x-acme-request-uuid` as a
 *    custom header when `data.requestUUID` is present.
 *  - `checkout.fraudShieldVerification` preserves the hardcoded
 *    `?pageSize=50&page=1` defaults from the legacy and appends any
 *    additional pagination params after them.
 */
import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";

import { AjsResponse, wrapAjs } from "../ajs-response";
import {
  PAGINATION_FIELDS,
  PAGINATION_SEARCH_FIELDS,
  buildParams,
} from "../http-params.util";

// ============= Base URLs =============
// Hardcoded for grep-ability: pasting a URL straight from the browser's
// Network tab into a code search will land you here.

const ORDERS_BASE_V1 = "/v1/api/orders";
const ORDERS_BASE_V2 = "/v2/api/orders";
// Legacy quirk: `search()`, `csv()` and `list()` are the only three endpoints
// where the legacy stripped the trailing `s` off `/orders` (via
// `this.service.substring(0, length - 1)`) and called `/v2/api/order/summaries…`
// (singular). The backend only registers the singular routes for these three.
const ORDER_SUMMARIES_BASE_V2 = "/v2/api/order/summaries";
const REBOOK_BASE_V1 = "/v1/api/rebook";
const WILLCALL_BASE_V1 = "/v1/api/willCall/orders";
const ORDERREQUEST_BASE_V2 = "/v2/api/orderrequest";
const ORDERFORMS_BASE_V2 = "/v2/api/orderforms";
const CHECKOUT_BASE_V2 = "/v2/api/orders/checkout";
const CHECKOUT_HARDCODED_V2 = "/v2/api/checkout"; // cancelAndRefund quirk
const PARTNERS_BASE_V2 = "/v2/api/orders/organizations";
const ANALYTICS_BASE_V2 = "/v2/api/orders/sync/resource";

// ============= Querystring whitelists =============
// Mirror the legacy `QueryStringBuilderFactory({ fields: { optional: [...] } })`
// contracts call-by-call. Keys outside the whitelist are silently dropped.

const ORDER_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "startDate",
  "endDate",
  "orderCategory",
] as const;

const DETAIL_BY_VOUCHER_FIELDS = ["voucherNumber", "organizationId"] as const;

const PARTNERS_LIST_FIELDS = [...PAGINATION_FIELDS] as const;

const WILLCALL_FIND_FIELDS = [
  "search",
  "orderNumber",
  "email",
  "contactName",
] as const;

const REQUESTS_LIST_FIELDS = [
  ...PAGINATION_SEARCH_FIELDS,
  "userId",
  "status",
  "paymentStatus",
] as const;

const ITEM_DETAILS_LIST_FIELDS = [...PAGINATION_FIELDS] as const;

const FORMS_LIST_FIELDS = ["pageSize", "page"] as const;

const FRAUD_SHIELD_FIELDS = ["pageSize", "page"] as const;

// ============= Helpers =============

interface CalculateBody {
  orderId: unknown;
  accountContactId: unknown;
  notes: unknown;
  orderItems: unknown;
  noEmail: unknown;
  email: unknown;
  contactFirstName: unknown;
  contactLastName: unknown;
  zipCode: unknown;
  phoneNumber: unknown;
}

function buildCalculateBody(props: any): CalculateBody {
  return {
    orderId: props.orderId,
    accountContactId: props.accountId,
    notes: props.notes,
    orderItems: props.orderItems,
    noEmail: props.noEmail,
    email: props.email,
    contactFirstName: props.contactFirstName,
    contactLastName: props.contactLastName,
    zipCode: props.zipCode,
    phoneNumber: props.phoneNumber,
  };
}

interface BookingCalculateBody {
  orderId: unknown;
  notes: unknown;
  rebookItems: unknown;
  incidentReasonCode: unknown;
  eventRebookFeeCounts: unknown;
}

function buildBookingCalculateBody(props: any): BookingCalculateBody {
  return {
    orderId: props.orderId,
    notes: props.notes,
    rebookItems: props.rebookItems,
    incidentReasonCode: props.incidentReasonCode,
    eventRebookFeeCounts: props.eventRebookFeeCounts,
  };
}

interface RequestItemUpdateBody {
  orderRequestId: unknown;
  orderRequestItemId: unknown;
  eventTemplateId: unknown;
  eventTemplateName: unknown;
  orderRequestItemSummary: unknown;
  orderItemType: unknown;
  customFields: unknown[];
  externalReferenceId: string;
  eventId?: unknown;
  eventStartDate?: unknown;
  eventName?: unknown;
}

function buildRequestItemUpdateBody(props: any): RequestItemUpdateBody {
  const out: RequestItemUpdateBody = {
    orderRequestId: props.orderRequestId,
    orderRequestItemId: props.orderRequestItemId,
    eventTemplateId: props.eventTemplateId,
    eventTemplateName: props.eventTemplateName,
    orderRequestItemSummary: props.orderRequestItemSummary,
    orderItemType: props.orderItemType,
    customFields: props.customFields || [],
    externalReferenceId: props.externalReferenceId || "",
  };
  if (props.eventId) out.eventId = props.eventId;
  if (props.eventStartDate) out.eventStartDate = props.eventStartDate;
  if (props.eventName) out.eventName = props.eventName;
  return out;
}

/**
 * If the input carries `requestUUID`, return a header dict for HttpClient;
 * otherwise return `undefined` so the request goes out without the header.
 * Matches the legacy behaviour where `headers: { 'x-acme-request-uuid': undefined }`
 * was effectively a no-op for `$http`.
 */
function requestUuidHeader(
  data: { requestUUID?: string } | null | undefined,
): { "x-acme-request-uuid": string } | undefined {
  return data?.requestUUID
    ? { "x-acme-request-uuid": data.requestUUID }
    : undefined;
}

// ============= Service =============

@Injectable({ providedIn: "root" })
export class OrdersApiService {
  private http = inject(HttpClient);

  // ─── Top-level methods ──────────────────────────────────────────────

  search(props?: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.get<any>(`${ORDER_SUMMARIES_BASE_V2}/search`, {
        params: buildParams(props, ORDER_LIST_FIELDS),
      }),
    );
  }

  /**
   * `useForm: true` triggers a full-page browser form POST (file download)
   * and resolves to `{ data: null }` so consumers can still chain `.then()`.
   * `useForm` falsy issues a JSON POST and returns the response. The legacy
   * service used jQuery to inject and submit a hidden form *and* returned
   * `undefined`, which broke the existing `.csv(...).then(...)` consumers at
   * runtime (latent bug). We use `document.createElement` to avoid the jQuery
   * dependency and wrap the download path in `Promise.resolve` so the chain
   * works as the consumer code clearly intended.
   */
  csv(
    props: Record<string, unknown> & { useForm?: boolean },
  ): Promise<AjsResponse<any>> {
    const params = buildParams(props, ORDER_LIST_FIELDS);
    const baseUrl = `${ORDER_SUMMARIES_BASE_V2}/csv`;
    if (props?.useForm) {
      const query = params.toString();
      const action = query ? `${baseUrl}?${query}` : baseUrl;
      const form = document.createElement("form");
      form.method = "post";
      form.enctype = "application/x-www-form-urlencoded";
      form.action = action;
      document.body.appendChild(form);
      form.submit();
      form.remove();
      return Promise.resolve({ data: null });
    }
    return wrapAjs(this.http.post<any>(baseUrl, null, { params }));
  }

  list(props?: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.get<any>(ORDER_SUMMARIES_BASE_V2, {
        params: buildParams(props, ORDER_LIST_FIELDS),
      }),
    );
  }

  detail(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${ORDERS_BASE_V1}/detail/${id}`));
  }

  detailByVoucher(props: Record<string, unknown>): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.get<any>(`${ORDERS_BASE_V1}/voucher`, {
        params: buildParams(props, DETAIL_BY_VOUCHER_FIELDS),
      }),
    );
  }

  transactions(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${ORDERS_BASE_V1}/${id}/transactions`));
  }

  /** Reads the static asset bundled with the app. */
  formsSummary(): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>("/assets/data/orderFormSummary.json"));
  }

  event(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${ORDERS_BASE_V1}/event/${id}`));
  }

  notes(props: { id: string; notes: string }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${ORDERS_BASE_V1}/${props.id}/notes`, {
        notes: props.notes,
      }),
    );
  }

  email(props: {
    id: string;
    email: string;
    preferredLanguage: string;
  }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${ORDERS_BASE_V1}/${props.id}/email`, {
        email: props.email,
        preferredLanguage: props.preferredLanguage,
        sendEmail: true,
      }),
    );
  }

  voucher(props: { id: string; paymentId: string }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${ORDERS_BASE_V1}/${props.id}/paymentid`, {
        paymentId: props.paymentId,
      }),
    );
  }

  externalReferenceId(props: {
    orderId: string | number;
    eventId: string | number;
    externalReferenceId: string;
  }): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(
        `${ORDERS_BASE_V2}/${props.orderId}/events/${props.eventId}/extreferenceid/${props.externalReferenceId}`,
        null,
      ),
    );
  }

  events(id: number | string): Promise<AjsResponse<any>> {
    return wrapAjs(this.http.get<any>(`${ORDERS_BASE_V1}/${id}/events`));
  }

  calculate(props: any): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(
        `${ORDERS_BASE_V1}/update/charges`,
        buildCalculateBody(props),
      ),
    );
  }

  update(data: any): Promise<AjsResponse<any>> {
    const headers = requestUuidHeader(data);
    return wrapAjs(
      this.http.post<any>(`${ORDERS_BASE_V1}/update`, data, { headers }),
    );
  }

  validateEntitlements(data: unknown): Promise<AjsResponse<any>> {
    return wrapAjs(
      this.http.post<any>(`${ORDERS_BASE_V1}/validateEntitlements`, data),
    );
  }

  // ─── Partners ────────────────────────────────────────────────────────

  readonly partners = {
    list: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(`${PARTNERS_BASE_V2}/summaries/${props.id}`, {
          params: buildParams(props, PARTNERS_LIST_FIELDS),
        }),
      ),
    // `create` writes to /v1/api/orders/organizations[/:accountId].
    // Propagates `x-acme-request-uuid` when the caller supplies it.
    create: (data: any): Promise<AjsResponse<any>> => {
      const url = `/v1/api/orders/organizations${data.accountId ? `/${data.accountId}` : ""}`;
      const headers = requestUuidHeader(data);
      return wrapAjs(this.http.post<any>(url, data, { headers }));
    },
  };

  // ─── Booking (rebook) ────────────────────────────────────────────────

  readonly booking = {
    create: (data: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(`${REBOOK_BASE_V1}/orders`, data)),
    calculate: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.post<any>(
          `${REBOOK_BASE_V1}/charges`,
          buildBookingCalculateBody(props),
        ),
      ),
  };

  // ─── Will-call ───────────────────────────────────────────────────────

  readonly willCall = {
    find: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(WILLCALL_BASE_V1, {
          params: buildParams(props, WILLCALL_FIND_FIELDS),
        }),
      ),
    order: (id: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${WILLCALL_BASE_V1}/${id}`)),
  };

  // ─── Order requests (deep nested) ────────────────────────────────────

  readonly requests = {
    list: (props?: Record<string, unknown>): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(ORDERREQUEST_BASE_V2, {
          params: buildParams(props, REQUESTS_LIST_FIELDS),
        }),
      ),
    detail: (id: number | string): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${ORDERREQUEST_BASE_V2}/${id}`)),
    create: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(ORDERREQUEST_BASE_V2, props)),
    update: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.put<any>(ORDERREQUEST_BASE_V2, props)),
    // Legacy DELETE carries `props` as body — preserved via `{ body }`.
    remove: (props: { id: number | string }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.delete<any>(`${ORDERREQUEST_BASE_V2}/${props.id}`, {
          body: props,
        }),
      ),

    items: {
      detail: (
        id: number | string,
        itemId: number | string,
      ): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(`${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}`),
        ),
      create: (
        id: number | string,
        props: unknown,
      ): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.post<any>(`${ORDERREQUEST_BASE_V2}/${id}/items`, props),
        ),
      update: (id: number | string, props: any): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.put<any>(
            `${ORDERREQUEST_BASE_V2}/${id}/items`,
            buildRequestItemUpdateBody(props),
          ),
        ),
      remove: (
        id: number | string,
        itemId: number | string,
      ): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.delete<any>(
            `${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}`,
          ),
        ),

      itemDetails: {
        list: (
          id: number | string,
          itemId: number | string,
          props?: Record<string, unknown>,
        ): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.get<any>(
              `${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}/details`,
              { params: buildParams(props, ITEM_DETAILS_LIST_FIELDS) },
            ),
          ),
        create: (
          id: number | string,
          itemId: number | string,
          props: unknown,
        ): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.post<any>(
              `${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}/details`,
              props,
            ),
          ),
        createBulk: (
          id: number | string,
          itemId: number | string,
          props: unknown,
        ): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.post<any>(
              `${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}/details/bulk`,
              props,
            ),
          ),
        remove: (
          id: number | string,
          itemId: number | string,
          itemDetailId: number | string,
        ): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.delete<any>(
              `${ORDERREQUEST_BASE_V2}/${id}/items/${itemId}/details/${itemDetailId}`,
            ),
          ),
      },
    },
  };

  // ─── Forms (deep nested) ─────────────────────────────────────────────

  readonly forms = {
    list: (props: any): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(
          `${ORDERFORMS_BASE_V2}/orders/${props.orderId}/forms/summaries`,
          { params: buildParams(props, FORMS_LIST_FIELDS) },
        ),
      ),
    response: (props: {
      orderId: string;
      responseId: string;
    }): Promise<AjsResponse<any>> =>
      wrapAjs(
        this.http.get<any>(
          `${ORDERFORMS_BASE_V2}/orders/${props.orderId}/forms/responses/${props.responseId}`,
        ),
      ),
    orders: {
      forms: {
        list: (props: { orderId: string }): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.get<any>(
              `${ORDERFORMS_BASE_V2}/orders/${props.orderId}/forms`,
            ),
          ),
        update: (props: {
          orderId: string;
          items: unknown;
        }): Promise<AjsResponse<any>> =>
          wrapAjs(
            this.http.put<any>(
              `${ORDERFORMS_BASE_V2}/orders/${props.orderId}/forms`,
              props.items,
            ),
          ),
      },
    },
    responses: {
      customer: (
        props: { customerId: string },
        queryParams: Record<string, string | number | boolean>,
      ): Promise<AjsResponse<any>> => {
        let params = new HttpParams();
        if (queryParams) {
          for (const [k, v] of Object.entries(queryParams)) {
            if (v == null || v === "") continue;
            params = params.set(k, String(v));
          }
        }
        return wrapAjs(
          this.http.get<any>(
            `${ORDERFORMS_BASE_V2}/responses/customer/${props.customerId}`,
            { params },
          ),
        );
      },
      membership: (props: {
        membershipId: string;
      }): Promise<AjsResponse<any>> =>
        wrapAjs(
          this.http.get<any>(
            `${ORDERFORMS_BASE_V2}/responses/membership/${props.membershipId}`,
          ),
        ),
      organization: {
        list: (
          props: { organizationId: string },
          queryParams: Record<string, string | number | boolean>,
        ): Promise<AjsResponse<any>> => {
          let params = new HttpParams();
          if (queryParams) {
            for (const [k, v] of Object.entries(queryParams)) {
              if (v == null || v === "") continue;
              params = params.set(k, String(v));
            }
          }
          return wrapAjs(
            this.http.get<any>(
              `${ORDERFORMS_BASE_V2}/responses/organization/${props.organizationId}`,
              { params },
            ),
          );
        },
      },
    },
  };

  // ─── Analytics ───────────────────────────────────────────────────────

  readonly analytics = {
    sync: (props: { eventId: string | number }): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.get<any>(`${ANALYTICS_BASE_V2}/${props.eventId}`)),
  };

  // ─── Checkout ────────────────────────────────────────────────────────

  readonly checkout = {
    charges: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(`${CHECKOUT_BASE_V2}/charges`, props)),
    // Hardcoded /v2/api/checkout (NOT under /v2/api/orders/checkout).
    cancelAndRefund: (props: unknown): Promise<AjsResponse<any>> =>
      wrapAjs(this.http.post<any>(CHECKOUT_HARDCODED_V2, props)),
    /**
     * Legacy preserved `?pageSize=50&page=1` defaults baked into the URL,
     * then appended any additional whitelisted params after them. Replicated
     * here verbatim — passing `pageSize`/`page` in `props` yields a duplicated
     * key in the querystring (matches legacy behaviour exactly).
     */
    fraudShieldVerification: (
      props: Record<string, unknown>,
    ): Promise<AjsResponse<any>> => {
      const extra = buildParams(props, FRAUD_SHIELD_FIELDS).toString();
      const url = extra
        ? `${CHECKOUT_BASE_V2}/3ds/verification?pageSize=50&page=1&${extra}`
        : `${CHECKOUT_BASE_V2}/3ds/verification?pageSize=50&page=1`;
      return wrapAjs(this.http.post<any>(url, props));
    },
  };
}
